/**
 * Centralized path configuration for the entire project
 * Use this file to manage all resource paths in one place
 *
 * This allows changing the base path globally by modifying window.__OPTIONS__.resources_path
 * or updating this file if needed.
 */

/**
 * Gets the base path from window.__OPTIONS__ or returns empty string
 * This is the root path for all resources (without /assets suffix)
 * @returns {string} Base path (e.g., "" or "/custom/path")
 */
export const getBasePath = () => {
  return `${window.__OPTIONS__?.resources_path || ""}`;
};

/**
 * Gets the assets path (base path + /assets)
 * Use this for assets like images, sounds, spines, fonts, etc.
 * @returns {string} Assets path (e.g., "/assets" or "/custom/path/assets")
 */
export const getAssetsPath = () => {
  return `${getBasePath()}/assets`;
};

/**
 * Gets the rules HTML path
 * @param {string} locale - Locale code (e.g., "en", "de", "fr-CA")
 * @param {string} fallbackLng - Fallback language (default: "en")
 * @returns {string} Path to rules HTML file
 */
export const getRulesPath = (locale, fallbackLng = "en") => {
  const basePath = getBasePath();
  if (locale === fallbackLng) {
    return `${basePath}/rules/rules.html`;
  }
  return `${basePath}/rules/locales/${locale}.html`;
};

/**
 * Gets the translations path for game namespace
 * @param {string} locale - Locale code (e.g., "en", "de", "fr-CA")
 * @param {string} fallbackLng - Fallback language (default: "en")
 * @returns {string} Path to translations JSON file
 */
export const getTranslationsPath = (locale, fallbackLng = "en") => {
  const basePath = getBasePath();
  if (locale === fallbackLng) {
    return `${basePath}/translations/translations.json`;
  }
  return `${basePath}/translations/locales/${locale}.json`;
};

/**
 * Gets the translations-common path
 * Supports theme-specific overrides (e.g., "slot-art" → translations-common-slot-art.json)
 * @param {string} locale - Locale code (e.g., "en", "de", "fr-CA")
 * @param {string} fallbackLng - Fallback language (default: "en")
 * @param {string} [theme] - Optional theme suffix (e.g., "slot-art")
 * @returns {string} Path to translations-common JSON file
 */
export const getTranslationsCommonPath = (locale, fallbackLng = "en", theme) => {
  const basePath = getBasePath();
  const filename = theme
    ? `translations-common-${theme}.json`
    : "translations-common.json";
  if (locale === fallbackLng) {
    return `${basePath}/translations-common/${filename}`;
  }
  return `${basePath}/translations-common/locales/${locale}.json`;
};

// Note: Do not export constants computed at module load time
// Always use the functions getBasePath() and getAssetsPath() instead
// This ensures paths are computed dynamically when needed
